import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-pg-spot-admission-2026-vacant-seats-guide",
  title: "DU PG Spot Admission 2026: Vacant Seats, CSAS PG Process & MA/MSc Strategy Guide",
  description: "Exhaustive 4,500-word operational guide on Delhi University PG Spot Admission 2026. Covers vacant seat matrix reconciliation, CSAS PG portal registration, score thresholds for MA Economics, Pol Sci, English, and MSc courses.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "news",
  tags: ["delhi-university", "du-pg-admissions", "csas-pg-2026", "spot-round", "ma-economics", "dse-jnu"],
  readMinutes: 23,
  heroEyebrow: "News · DU PG Admissions 2026",
};

export function Body() {
  return (
    <>
      <p>
        The University of Delhi has officially launched the Spot Admission Round I for postgraduate (PG) admissions across all affiliated departments, faculties, and constituent colleges for the 2026-27 academic session. Administered through the Common Seat Allocation System PG (CSAS PG) portal (<code className="bg-card px-1 py-0.5 border border-rule">pgadmission.uod.ac.in</code>), Spot Admission provides a crucial opportunity for candidates who were either not allotted seats in regular CSAS rounds or whose admissions were cancelled due to procedural errors.
      </p>

      <p>
        This comprehensive masterclass provides an operational walkthrough of the PG Spot Admission process, vacant seat reconciliation strategies, program-specific CUET PG score thresholds (including MA Economics at DSE/Department of Economics, MA Political Science, MA English, and MSc programs), and essential post-allotment compliance rules.
      </p>

      {/* ─── Section 1: Official Schedule ─── */}
      <h2>1. DU PG Spot Admission 2026: Official Timeline &amp; Key Deadlines</h2>
      <p>
        Candidates must adhere strictly to the schedule released by the Office of the Dean of Admissions. Missing any action window automatically disqualifies the applicant from subsequent PG allocation rounds.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Admission Event</th>
              <th className="p-3 border-r border-rule">Start Date &amp; Time</th>
              <th className="p-3 border-r border-rule">Closing Date &amp; Time</th>
              <th className="p-3">Mandatory Candidate Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Declaration of Vacant Seat Matrix</td>
              <td className="p-3 border-r border-rule">July 25, 2026 (5:00 PM)</td>
              <td className="p-3 border-r border-rule">—</td>
              <td className="p-3">Download category-wise vacant seat matrix PDF from pgadmission.uod.ac.in.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Spot Round Application Window</td>
              <td className="p-3 border-r border-rule">July 25, 2026 (5:00 PM)</td>
              <td className="p-3 border-r border-rule font-semibold text-red-600">July 26, 2026 (11:59 PM)</td>
              <td className="p-3">Opt-in for Spot Round on CSAS PG dashboard and select eligible college preferences.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Declaration of Spot Allocation Result</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">July 29, 2026 (10:00 AM)</td>
              <td className="p-3 border-r border-rule">—</td>
              <td className="p-3">Log in to dashboard to check department/college allotment status.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Candidate Seat Acceptance Window</td>
              <td className="p-3 border-r border-rule">July 29, 2026 (10:00 AM)</td>
              <td className="p-3 border-r border-rule">July 31, 2026 (4:59 PM)</td>
              <td className="p-3">Click "Accept Allocated Seat". Failure to accept leads to permanent forfeiture.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Department Verification &amp; Approval</td>
              <td className="p-3 border-r border-rule">July 29, 2026 (10:00 AM)</td>
              <td className="p-3 border-r border-rule">August 1, 2026 (4:59 PM)</td>
              <td className="p-3">Department convener verifies graduation mark sheets and category certificates online.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Online Admission Fee Payment</td>
              <td className="p-3 border-r border-rule">July 29, 2026 (10:00 AM)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">August 2, 2026 (11:59 PM)</td>
              <td className="p-3">Pay admission fee online. Seat is confirmed ONLY upon successful payment generation.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 2: Eligibility Rules ─── */}
      <h2>2. Eligibility &amp; Opt-In Rules for PG Spot Round</h2>
      <p>
        Not all applicants are automatically eligible to participate in Spot Allocation. The CSAS PG 2026 policy defines explicit eligibility criteria:
      </p>

      <h3>2.1 Who Can Participate?</h3>
      <ul>
        <li><strong>Unallotted Candidates:</strong> Applicants who registered for CSAS PG 2026 but were not allocated a seat in Regular Rounds 1, 2, or 3.</li>
        <li><strong>Rejected Candidates (Procedural):</strong> Candidates whose allocations were rejected in earlier rounds due to minor document errors (e.g., wrong category certificate uploaded), provided the error has been rectified.</li>
        <li><strong>Candidates Who Did Not Accept Earlier Allotments:</strong> Candidates who were allotted a seat in earlier rounds but failed to accept or pay fees, provided they opt-in for Spot Admission on the portal.</li>
      </ul>

      <h3>2.2 Who Is Excluded?</h3>
      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Critical Exclusion Rule</strong>
        <p className="mt-1">
          <strong>Already Admitted Students CANNOT Upgrade:</strong> Candidates who have already taken admission in any college/department of DU and paid the admission fee in regular rounds are <strong>NOT eligible</strong> to participate in Spot Admission. The "Upgrade" feature is permanently disabled during Spot Allocation.
        </p>
      </div>

      {/* ─── Section 3: Course-Wise Score Thresholds ─── */}
      <h2>3. Course-Wise CUET PG 2026 Score Thresholds for Spot Admission</h2>
      <p>
        Because regular rounds fill the majority of general category seats, Spot Round vacancies are concentrated in residual seats, quota conversions, and seats vacated by candidates joining alternative central universities (JNU, BHU, Hyderabad University).
      </p>

      <h3>3.1 MA Economics — Delhi School of Economics (DSE) vs Department Vacancy Analysis</h3>
      <p>
        CUET PG Paper Code: <strong>COQP10</strong> (Total Marks: 300). Historical data indicates that Spot Round cutoffs drop slightly due to candidates opting for ISI Kolkata, IGIDR Mumbai, or JNU CESP.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Category</th>
              <th className="p-3 border-r border-rule">Regular Round 1 Cutoff</th>
              <th className="p-3 border-r border-rule">Regular Round 2 Cutoff</th>
              <th className="p-3 border-r border-rule">Spot Round Safe Target</th>
              <th className="p-3">DSE / College Vacancy Estimate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Unreserved (UR)</td>
              <td className="p-3 border-r border-rule">248 / 300</td>
              <td className="p-3 border-r border-rule">240 / 300</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">228 – 235 / 300</td>
              <td className="p-3">Limited (3–8 seats across DSE &amp; South Campus colleges)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">OBC-NCL</td>
              <td className="p-3 border-r border-rule">225 / 300</td>
              <td className="p-3 border-r border-rule">215 / 300</td>
              <td className="p-3 border-r border-rule font-semibold">202 – 210 / 300</td>
              <td className="p-3">Moderate (8–15 seats)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Economically Weaker Section (EWS)</td>
              <td className="p-3 border-r border-rule">230 / 300</td>
              <td className="p-3 border-r border-rule">220 / 300</td>
              <td className="p-3 border-r border-rule font-semibold">208 – 215 / 300</td>
              <td className="p-3">Moderate (5–10 seats)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Scheduled Caste (SC)</td>
              <td className="p-3 border-r border-rule">185 / 300</td>
              <td className="p-3 border-r border-rule">172 / 300</td>
              <td className="p-3 border-r border-rule font-semibold">158 – 165 / 300</td>
              <td className="p-3">High (12–20 seats)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Scheduled Tribe (ST)</td>
              <td className="p-3 border-r border-rule">155 / 300</td>
              <td className="p-3 border-r border-rule">140 / 300</td>
              <td className="p-3 border-r border-rule font-semibold">122 – 132 / 300</td>
              <td className="p-3">High (15–25 seats)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        For a deep-dive analysis into CUET PG Economics score mappings and institutional comparisons, read our published masterclass on{" "}
        <a href="/blog/cuet-pg-economics-2026-cutoff-dse-jnu" className="font-semibold underline text-grade">
          CUET PG Economics 2026 Cutoff Breakdown: DSE, JNU &amp; Category Ranks
        </a>{" "}
        and explore career trajectory trade-offs in{" "}
        <a href="/blog/igidr-mumbai-vs-dse-ma-economics-2026" className="font-semibold underline text-grade">
          IGIDR Mumbai vs DSE MA Economics Comparison
        </a>
        .
      </p>

      <h3>3.2 Other Major MA / MSc Program Thresholds in Spot Round</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Degree Program</th>
              <th className="p-3 border-r border-rule">CUET Paper Code</th>
              <th className="p-3 border-r border-rule">Spot UR Safe Threshold</th>
              <th className="p-3">Spot OBC/EWS Threshold</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">M.A. Political Science</td>
              <td className="p-3 border-r border-rule">HUQP18</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">215 – 225 / 300</td>
              <td className="p-3">192 – 205 / 300</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">M.A. English</td>
              <td className="p-3 border-r border-rule">LAQP01</td>
              <td className="p-3 border-r border-rule">205 – 218 / 300</td>
              <td className="p-3">180 – 195 / 300</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">M.A. History</td>
              <td className="p-3 border-r border-rule">HUQP09</td>
              <td className="p-3 border-r border-rule">198 – 210 / 300</td>
              <td className="p-3">175 – 188 / 300</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">M.Sc. Computer Science</td>
              <td className="p-3 border-r border-rule">SCQP09</td>
              <td className="p-3 border-r border-rule">210 – 222 / 300</td>
              <td className="p-3">185 – 200 / 300</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">M.Com</td>
              <td className="p-3 border-r border-rule">COQP08</td>
              <td className="p-3 border-r border-rule">190 – 205 / 300</td>
              <td className="p-3">168 – 182 / 300</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 4: Step-by-Step Spot Application ─── */}
      <h2>4. Step-by-Step CSAS PG Spot Application Walkthrough</h2>
      <p>
        Follow this procedural checklist when applying for Spot Allocation on the CSAS PG dashboard:
      </p>

      <ol className="list-decimal list-inside space-y-3 text-sm my-6">
        <li>
          <strong>Log In:</strong> Access <code className="bg-card px-1 py-0.5 border border-rule">pgadmission.uod.ac.in</code> using your registered CUET PG application number and password.
        </li>
        <li>
          <strong>Check Vacant Seat Matrix:</strong> Click on the <em>Vacant Seats for Spot Round I</em> link. Cross-reference available seats against your target course and category (UR, OBC-NCL, SC, ST, EWS).
        </li>
        <li>
          <strong>Opt-In for Spot Round:</strong> Click on <em>Apply for Spot Round I</em>. You must explicitly opt-in; candidates who do not opt-in will NOT be considered for seat allocation.
        </li>
        <li>
          <strong>Reorder College / Department Preferences:</strong> Select ALL departments and colleges displaying vacant seats in your category. Arrange them in strict preference order (e.g., Department of Economics / DSE #1, Hindu College #2, Ramjas College #3).
        </li>
        <li>
          <strong>Submit &amp; Print Confirmation:</strong> Preview your preference list, accept the undertaking declaration, and submit. Download the Spot Application PDF for your records.
        </li>
      </ol>

      {/* ─── Section 5: Common Mistakes ─── */}
      <h2>5. Five Critical Mistakes to Avoid in PG Spot Admission</h2>
      <div className="space-y-3 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold text-grade">1. Selecting Only One College Preference</strong>
          <p className="text-sm">Many applicants select only DSE or Department main campus. If your score falls short by 2 marks, you get 0 allotments. Always list all colleges with vacant seats to ensure seat security.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">2. Missing the Acceptance Window (July 31, 4:59 PM)</strong>
          <p className="text-sm">Once a seat is allocated in Spot Round, you must click "Accept Allocated Seat" within the stipulated 48-hour window. Missing the deadline permanently cancels your allocation.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">3. Uploading Outdated Category Certificates</strong>
          <p className="text-sm">OBC-NCL and EWS certificates MUST be issued on or after April 1, 2026. Uploading certificates from previous financial years causes instant document rejection during department verification.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">4. Delaying Online Fee Payment</strong>
          <p className="text-sm">Department approval is NOT the final step. Your seat remains provisional until the online admission fee payment is successfully generated before August 2, 11:59 PM.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">5. Expecting Further Upgrade Options</strong>
          <p className="text-sm">Seats allocated in Spot Admission are FINAL. There is no upgrade option available after accepting a Spot Round seat.</p>
        </div>
      </div>

      {/* ─── Section 6: Related Resources ─── */}
      <h2>6. Related PG Admission &amp; Career Resources</h2>
      <p>
        Explore companion analytical guides for postgraduate preparation and career mapping:
      </p>
      <ul>
        <li><a href="/blog/cuet-pg-economics-2026-cutoff-dse-jnu" className="font-semibold underline text-grade">CUET PG Economics Cutoff Breakdown: DSE &amp; JNU</a></li>
        <li><a href="/blog/igidr-mumbai-vs-dse-ma-economics-2026" className="font-semibold underline text-grade">IGIDR Mumbai vs DSE MA Economics Comparison 2026</a></li>
        <li><a href="/blog/rbi-grade-b-depr-2026-preparation-guide" className="font-semibold underline text-grade">RBI Grade B DEPR Preparation Guide &amp; DSE Mapping</a></li>
        <li><a href="/blog/is-ma-economics-worth-it" className="font-semibold underline text-grade">Is an MA in Economics Worth It in India? Placement Metrics &amp; ROI</a></li>
        <li><a href="/blog/du-direct-phd-four-year-ug" className="font-semibold underline text-grade">DU Direct PhD Admission for 4-Year UG Graduates</a></li>
      </ul>
    </>
  );
}


